import { getAdminBaseUrl } from './admin-url';

export interface MimeTypeError {
  url: string;
  expected: string;
  received: string;
  type: 'script' | 'css' | 'other';
  timestamp: Date;
}

export interface NetworkDiagnostic {
  url: string;
  status: number;
  statusText: string;
  mimeType: string | null;
  cors: boolean | null;
  accessible: boolean;
  redirected: boolean;
  finalUrl?: string;
  error?: string;
}

export class MimeTypeDiagnostic {
  private errors: MimeTypeError[] = [];
  private originalFetch: typeof fetch;

  constructor() {
    this.originalFetch = window.fetch;
    this.interceptFetch();
    this.interceptScriptLoading();
    this.interceptCSSLoading();
  }

  private interceptFetch() {
    const originalFetch = this.originalFetch;

    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      try {
        const response = await originalFetch.call(window, input, init);
        return response;
      } catch (error) {
        throw error;
      }
    };
  }

  private interceptScriptLoading() {
    const originalCreateElement = document.createElement;
    const self = this;

    document.createElement = function (tagName: string) {
      const element = originalCreateElement.call(document, tagName);

      if (tagName.toLowerCase() === 'script') {
        const script = element as HTMLScriptElement;
        const originalSrcSetter = Object.getOwnPropertyDescriptor(HTMLScriptElement.prototype, 'src')?.set;

        if (originalSrcSetter) {
          Object.defineProperty(script, 'src', {
            set: function (value: string) {
              originalSrcSetter.call(this, value);
            },
            get: function () {
              return this.getAttribute('src');
            },
          });
        }

        script.addEventListener('error', async (event) => {
          let actualMimeType = 'text/html';
          try {
            const response = await fetch(script.src, { method: 'HEAD' });
            actualMimeType = response.headers.get('content-type') || 'text/html';
          } catch (e) {}

          const error: MimeTypeError = {
            url: script.src,
            expected: 'application/javascript',
            received: actualMimeType.split(';')[0],
            type: 'script',
            timestamp: new Date(),
          };
          self.logMimeError(error);
        });
      }

      return element;
    };
  }

  private interceptCSSLoading() {
    const originalCreateElement = document.createElement;
    const self = this;

    document.createElement = function (tagName: string) {
      const element = originalCreateElement.call(document, tagName);

      if (tagName.toLowerCase() === 'link') {
        const link = element as HTMLLinkElement;

        link.addEventListener('error', async (event) => {
          if (link.rel === 'stylesheet') {
            let actualMimeType = 'text/html';
            try {
              const response = await fetch(link.href, { method: 'HEAD' });
              actualMimeType = response.headers.get('content-type') || 'text/html';
            } catch (e) {}

            const error: MimeTypeError = {
              url: link.href,
              expected: 'text/css',
              received: actualMimeType.split(';')[0],
              type: 'css',
              timestamp: new Date(),
            };
            self.logMimeError(error);
          }
        });
      }

      return element;
    };
  }

  private logMimeError(error: MimeTypeError) {
    this.errors.push(error);
  }

  public async diagnoseUrl(url: string): Promise<NetworkDiagnostic> {
    const diagnostic: NetworkDiagnostic = {
      url,
      status: 0,
      statusText: '',
      mimeType: null,
      cors: null,
      accessible: false,
      redirected: false,
    };

    try {
      const headResponse = await this.originalFetch.call(window, url, { method: 'HEAD' });

      diagnostic.status = headResponse.status;
      diagnostic.statusText = headResponse.statusText;
      diagnostic.mimeType = headResponse.headers.get('content-type');
      diagnostic.accessible = headResponse.ok;
      diagnostic.redirected = headResponse.redirected;
      diagnostic.finalUrl = headResponse.url;

      // Additional check: if status is 200 but MIME type is HTML for JS files, it's likely a 404 page
      if (url.endsWith('.js') && diagnostic.mimeType?.includes('text/html') && headResponse.status === 200) {
        diagnostic.accessible = false;
        diagnostic.error = 'File not found (server returned HTML instead of JavaScript)';
      }

      const corsHeader = headResponse.type === 'cors';
      diagnostic.cors = corsHeader;
    } catch (error) {
      diagnostic.error = error instanceof Error ? error.message : 'Unknown error';
    }

    return diagnostic;
  }

  public getErrors(): MimeTypeError[] {
    return [...this.errors];
  }

  public clearErrors(): void {
    this.errors = [];
  }

  public generateReport(): string {
    const report = [
      '🔍 MIME Type Diagnostic Report',
      '================================',
      '',
      `Total errors: ${this.errors.length}`,
      '',
    ];

    if (this.errors.length === 0) {
      report.push('✅ No MIME type errors detected!');
    } else {
      this.errors.forEach((error, index) => {
        report.push(`${index + 1}. ${error.type.toUpperCase()} Error:`);
        report.push(`   URL: ${error.url}`);
        report.push(`   Expected: ${error.expected}`);
        report.push(`   Received: ${error.received}`);
        report.push(`   Time: ${error.timestamp.toISOString()}`);
        report.push('');
      });

      report.push('🔧 Common Solutions:');
      report.push('- Check if the server is running and accessible');
      report.push('- Verify build output and asset paths');
      report.push('- Check server MIME type configuration');
      report.push('- Ensure proper routing (no fallback to index.html for assets)');
      report.push('- Verify CORS headers');
    }

    return report.join('\n');
  }

  public async diagnoseCommonAssets(baseUrl?: string): Promise<NetworkDiagnostic[]> {
    const origin = baseUrl ?? getAdminBaseUrl();
    const commonPaths = ['/remoteEntry.js'];

    const diagnostics = await Promise.all(commonPaths.map((path) => this.diagnoseUrl(origin + path)));

    return diagnostics;
  }

  public destroy() {
    window.fetch = this.originalFetch;
  }
}

export const mimeTypeDiagnostic = new MimeTypeDiagnostic();

export async function diagnoseMFIssues(remoteBaseUrl: string) {
  // Extract base URL if a full remoteEntry.js URL is provided
  const baseUrl = remoteBaseUrl.endsWith('/remoteEntry.js')
    ? remoteBaseUrl.replace('/remoteEntry.js', '')
    : remoteBaseUrl;

  const diagnostics = await mimeTypeDiagnostic.diagnoseCommonAssets(baseUrl);

  return {
    diagnostics,
    report: mimeTypeDiagnostic.generateReport(),
    errors: mimeTypeDiagnostic.getErrors(),
  };
}
